import { ClientDirectoryClient } from '../../clients/services/clientDirectoryService';

export interface ClientMatchResult {
  matches: boolean;
  rank: number;
}

const NO_MATCH: ClientMatchResult = { matches: false, rank: 99 };

/**
 * Convert a stored "Last, First" name into "First Last" for display/matching.
 * Mirrors ClientOmnibox.transformName.
 */
export function transformClientName(name: string | null | undefined): string {
  if (!name) return 'Unknown';
  if (name.includes(',')) {
    const [lastName, firstName] = name.split(',').map(part => part.trim());
    return `${firstName} ${lastName}`;
  }
  return name;
}

/**
 * Pure match + rank for a single client against a query.
 * Matches on transformed name, client_id, AND email (the new field per the spec).
 * Canonical rank (lower = better): id exact 0, id prefix 1, name prefix 2,
 * email prefix 3, substring 4.
 */
export function clientMatches(client: ClientDirectoryClient, query: string): ClientMatchResult {
  const q = query.trim().toLowerCase();
  if (!q) return NO_MATCH;

  const name = transformClientName(client.client_name).toLowerCase();
  const id = (client.client_id ?? '').toLowerCase();
  const email = (client.email ?? '').toLowerCase();

  const nameHit = name.includes(q);
  const idHit = id.includes(q);
  const emailHit = email.includes(q);

  if (!nameHit && !idHit && !emailHit) return NO_MATCH;

  let rank: number;
  if (id === q) rank = 0;
  else if (id.startsWith(q)) rank = 1;
  else if (name.startsWith(q)) rank = 2;
  else if (email.startsWith(q)) rank = 3;
  else rank = 4;

  return { matches: true, rank };
}

/**
 * Filter + rank a list of clients for a query.
 * Sorted by rank, then alphabetically by transformed name; capped at `limit` (default 10).
 */
export function rankClients(
  clients: ClientDirectoryClient[],
  query: string,
  limit = 10,
): ClientDirectoryClient[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  return clients
    .map(client => ({
      client,
      result: clientMatches(client, query),
      name: transformClientName(client.client_name).toLowerCase(),
    }))
    .filter(entry => entry.result.matches)
    .sort((a, b) => a.result.rank - b.result.rank || a.name.localeCompare(b.name))
    .slice(0, limit)
    .map(entry => entry.client);
}
